import React, { useRef, useReducer, useMemo, useEffect, useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import { Transition } from 'react-transition-group';
import { BaseTooltip } from './BaseTooltip';
import { PortalBaseTooltip } from './PortalBaseTooltip';
import { useHideOnClickOutside } from 'hooks/useHideOnClickOutside';
import { noop } from 'utils';

interface IHidingTooltipProps {
  enabled?: boolean;
  children: React.ReactNode;
  renderTooltipContent: ({ isOpen }: { isOpen: boolean }) => React.ReactNode;
  position: TooltipPosition;
  width?: string;
  tooltipClassname?: string;
  tooltipWrapperClassname?: string;
  notRelativeClassName?: boolean;
  elementClassname?: string;
  testId?: string;
  showShadow?: boolean;
  onRenderCallback?: () => void;
  usePortal?: boolean;
  keepTooltipOnHover?: boolean;
  showOnClick?: boolean;
  hideOnClick?: boolean;
  hideOnClickOutside?: boolean;
  notifyOnClickOutside?: () => void;
  notifyOpenClosedState?: (isOpen: boolean) => void;
}

interface ITooltipCoords {
  x: number;
  y: number;
}

export type TooltipPosition =
  | 'top'
  | 'bottom'
  | 'bottom-right'
  | 'bottom-left'
  | 'left'
  | 'left-top'
  | 'left-bottom'
  | 'right'
  | 'right-top'
  | 'right-bottom'
  | 'bottom-direct'
  | 'bottom-screen';

interface ITooltipState {
  visible: boolean;
  coords?: ITooltipCoords;
}

interface ITooltipAction {
  type: 'HIDE_TOOLTIP' | 'SHOW_TOOLTIP';
  coords?: ITooltipCoords;
}

const getTooltipCoordsByPosition = (
  position: TooltipPosition,
  tooltipAnchorElement: HTMLDivElement,
  tooltipElement: HTMLDivElement
) => {
  const TOOLTIP_OFFSET_TOP = 7;
  const TOOLTIP_OFFSET_BOTTOM = 2;
  const TOOLTIP_OFFSET_LEFT = 7;
  const TOOLTIP_OFFSET_RIGHT = 5;

  const bodyRect = document.body.getBoundingClientRect();
  const tooltipAnchorRect = tooltipAnchorElement.getBoundingClientRect();
  const tooltipRect = tooltipElement.getBoundingClientRect();

  // This is to account for when a modal is open. See src/ui/GeneralModal/index.tsx
  const bodyTopValue = parseInt(document.body.style.top || '0');

  const offsetY = tooltipAnchorRect.top - bodyRect.y - window.scrollY + bodyTopValue;
  const offsetX = tooltipAnchorRect.left - bodyRect.x - window.scrollX;

  switch (position) {
    case 'top':
    default:
      return {
        x: offsetX - tooltipRect.width / 2 + tooltipAnchorRect.width / 2,
        y: offsetY - tooltipRect.height - TOOLTIP_OFFSET_TOP,
      };
    case 'left':
      return {
        x: offsetX - tooltipRect.width - TOOLTIP_OFFSET_LEFT,
        y: offsetY + tooltipAnchorRect.height / 2 - tooltipRect.height / 2,
      };
    case 'left-top':
      return {
        x: offsetX - tooltipRect.width - TOOLTIP_OFFSET_LEFT,
        y: offsetY,
      };
    case 'left-bottom':
      return {
        x: offsetX - tooltipRect.width - TOOLTIP_OFFSET_LEFT,
        y: offsetY + tooltipAnchorRect.height + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'right':
      return {
        x: offsetX + tooltipAnchorRect.width + TOOLTIP_OFFSET_RIGHT,
        y: offsetY + tooltipAnchorRect.height / 2 - tooltipRect.height / 2,
      };
    case 'right-top':
      return {
        x: offsetX + tooltipAnchorRect.width + TOOLTIP_OFFSET_RIGHT,
        y: offsetY,
      };
    case 'right-bottom':
      return {
        x: offsetX + tooltipAnchorRect.width + TOOLTIP_OFFSET_RIGHT,
        y: offsetY + tooltipAnchorRect.height + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'bottom':
      return {
        x: offsetX - tooltipRect.width / 2 + tooltipAnchorRect.width / 2,
        y: offsetY + tooltipAnchorRect.height + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'bottom-right':
      return {
        x: offsetX + tooltipAnchorRect.width / 2,
        y: offsetY + tooltipAnchorRect.height + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'bottom-left':
      return {
        x: offsetX - tooltipRect.width + tooltipAnchorRect.width,
        y: offsetY + tooltipAnchorRect.height + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'bottom-direct':
      return {
        x: offsetX,
        y: offsetY + tooltipAnchorRect.height + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'bottom-screen':
      return {
        x: 20,
        y: offsetY + tooltipAnchorRect.height + TOOLTIP_OFFSET_BOTTOM,
      };
  }

  // THESE ARE THE OLD WAYS OF CALCULATING THE POSITION OF THE TOOLTIP
  switch (position) {
    case 'top':
      return {
        x: tooltipAnchorElement.offsetLeft - tooltipElement.clientWidth / 2 + tooltipAnchorElement.clientWidth / 2,
        y: tooltipAnchorElement.offsetTop - tooltipElement.clientHeight - TOOLTIP_OFFSET_TOP,
      };
    case 'bottom':
      return {
        x: tooltipAnchorElement.offsetLeft - tooltipElement.clientWidth / 2 + tooltipAnchorElement.clientWidth / 2,
        y: tooltipAnchorElement.offsetTop + tooltipAnchorElement.clientHeight + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'bottom-right':
      return {
        x: tooltipAnchorElement.offsetLeft,
        y: tooltipAnchorElement.offsetTop + tooltipAnchorElement.clientHeight + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'left':
      return {
        x: tooltipAnchorElement.offsetLeft - tooltipElement.clientWidth - TOOLTIP_OFFSET_LEFT,
        y: tooltipAnchorElement.offsetTop + tooltipAnchorElement.clientHeight / 2 - tooltipElement.clientHeight / 2,
      };
    case 'right':
      return {
        x: tooltipAnchorElement.offsetLeft + tooltipAnchorElement.clientWidth + TOOLTIP_OFFSET_RIGHT,
        y: tooltipAnchorElement.offsetTop + tooltipAnchorElement.clientHeight / 2 - tooltipElement.clientHeight / 2,
      };
    case 'right-bottom':
      return {
        x: tooltipAnchorElement.offsetLeft + tooltipAnchorElement.clientWidth + TOOLTIP_OFFSET_RIGHT,
        y: tooltipAnchorElement.offsetTop,
      };
    case 'bottom-left':
      return {
        x: tooltipAnchorElement.offsetLeft - tooltipElement.clientWidth + tooltipAnchorElement.clientWidth,
        y: tooltipAnchorElement.offsetTop + tooltipAnchorElement.clientHeight + TOOLTIP_OFFSET_BOTTOM,
      };
    case 'left-bottom':
      return {
        x: tooltipAnchorElement.offsetLeft - tooltipElement.clientWidth - TOOLTIP_OFFSET_LEFT,
        y: tooltipAnchorElement.offsetTop,
      };
    default:
      return {
        x: tooltipAnchorElement.offsetLeft - tooltipElement.clientWidth / 2 + tooltipAnchorElement.clientWidth / 2,
        y: tooltipAnchorElement.offsetTop + tooltipElement.clientHeight + 2,
      };
  }
};

const reducer = (state: ITooltipState, action: ITooltipAction) => {
  switch (action.type) {
    case 'HIDE_TOOLTIP':
      return { ...state, visible: false };
    case 'SHOW_TOOLTIP':
      return { visible: true, coords: action.coords };
  }
};

export const HidingTooltip: React.FC<IHidingTooltipProps> = React.memo(
  ({
    enabled = true,
    children,
    renderTooltipContent,
    position,
    tooltipClassname,
    elementClassname = '',
    tooltipWrapperClassname = '',
    testId = 'hiding-tool-tip',
    showShadow = true,
    width,
    onRenderCallback,
    notRelativeClassName = false,
    usePortal = false,
    keepTooltipOnHover = false,
    showOnClick = false,
    hideOnClick = false,
    hideOnClickOutside = false,
    notifyOnClickOutside,
    notifyOpenClosedState,
  }) => {
    const [state, dispatch] = useReducer(reducer, { visible: false, coords: undefined });
    const [isTouchInteraction, setIsTouchInteraction] = useState(false);
    const tooltipRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
      if (typeof window === 'undefined') {
        return;
      }

      const mediaQuery = window.matchMedia ? window.matchMedia('(hover: none)') : null;

      const updateTouchInteraction = () => {
        const hasTouchPoints = typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0;
        const hasNoHover = mediaQuery ? mediaQuery.matches : false;
        setIsTouchInteraction(hasTouchPoints || hasNoHover);
      };

      updateTouchInteraction();

      if (!mediaQuery) {
        return;
      }

      mediaQuery.addEventListener('change', updateTouchInteraction);

      return () => {
        mediaQuery.removeEventListener('change', updateTouchInteraction);
      };
    }, []);

    const handleMouseLeave = () => {
      if (hideOnClick) {
        return;
      }
      hideTooltip();
    };

    const showTooltip = (element: HTMLDivElement) => {
      if (state.visible || !tooltipRef.current || !enabled) {
        return;
      }
      if (!element) {
        throw new Error('Tooltip element is not found');
      }
      const tooltip = tooltipRef.current;
      const tooltipCoords = getTooltipCoordsByPosition(position, element, tooltip);
      dispatch({ type: 'SHOW_TOOLTIP', coords: tooltipCoords });
      if (notifyOpenClosedState) {
        notifyOpenClosedState(true);
      }
    };
    const hideTooltip = (_?: boolean) => {
      // unused parameter to use this function inside useHideOnClickOutside
      if (!state.visible) {
        return;
      }
      dispatch({ type: 'HIDE_TOOLTIP' });
      if (notifyOpenClosedState) {
        notifyOpenClosedState(false);
      }
    };
    const proxyNotifyOnClickOutside = useCallback(() => {
      if (state.visible && notifyOnClickOutside) {
        notifyOnClickOutside();
      }
    }, [notifyOnClickOutside, state.visible]);

    const shouldHideOnOutsideInteraction = hideOnClickOutside || isTouchInteraction;
    const { instanceClassname } = useHideOnClickOutside(
      shouldHideOnOutsideInteraction ? hideTooltip : noop,
      'tooltip',
      proxyNotifyOnClickOutside
    );

    const handleMouseEnter = (event: React.MouseEvent<HTMLDivElement>) => {
      if (isTouchInteraction) {
        return;
      }
      if (showOnClick) {
        return;
      }
      showTooltip(event.currentTarget.children[0] as HTMLDivElement);
    };

    const toggleOnClick = (event: React.MouseEvent<HTMLDivElement>) => {
      if (isTouchInteraction && !showOnClick) {
        if (!state.visible) {
          showTooltip(event.currentTarget.children[0] as HTMLDivElement);
        } else {
          hideTooltip();
        }
        return;
      }

      if (!state.visible && showOnClick) {
        showTooltip(event.currentTarget.children[0] as HTMLDivElement);
      } else if (state.visible && hideOnClick) {
        hideTooltip();
      } else if (state.visible && !showOnClick) {
        hideTooltip();
      }
    };

    const tooltipDefaultStyle = useMemo(() => ({ transition: 'opacity 300ms ease-in-out', opacity: 0 }), []);
    const tooltipTransitionStyles = useMemo(
      () => ({
        entering: { opacity: 1, zIndex: 1 },
        entered: { opacity: 1, zIndex: 1 },
        exiting: { opacity: 0, zIndex: 1 },
        exited: { opacity: 0, zIndex: -1 },
      }),
      []
    );

    useEffect(() => {
      onRenderCallback ? onRenderCallback() : null;
    }, []);

    const handleWrapperMouseEnter = (e: React.MouseEvent) => {
      if (isTouchInteraction) {
        return;
      }
      handleMouseEnter(e as any);
    };
    const handleWrapperMouseLeave = () => {
      if (isTouchInteraction) {
        return;
      }
      handleMouseLeave();
    };

    return (
      <div
        data-testid={testId}
        className={`${
          notRelativeClassName ? '' : 'relative'
        } tooltip-wrapper ${tooltipWrapperClassname} ${instanceClassname}`}
        onMouseEnter={keepTooltipOnHover ? handleWrapperMouseEnter : undefined}
        onMouseLeave={keepTooltipOnHover ? handleWrapperMouseLeave : undefined}
      >
        <div
          onMouseEnter={!keepTooltipOnHover ? handleMouseEnter : undefined}
          onMouseLeave={!keepTooltipOnHover ? handleMouseLeave : undefined}
          className={`element-with-tooltip flex ${elementClassname}`}
          onClick={toggleOnClick}
        >
          {children}
        </div>

        <div className="tooltip">
          <Transition in={state.visible} appear timeout={300} nodeRef={tooltipRef}>
            {tooltipAnimationState => {
              const tooltipProps = {
                wrapperClassName: `border border-solid border-gray-40 rounded bg-white ${
                  showShadow ? 'shadow-util1' : ''
                } ${tooltipClassname || ''}`,
                position: state.coords,
                tooltipRef: tooltipRef,
                style: { ...tooltipDefaultStyle, ...tooltipTransitionStyles[tooltipAnimationState] },
                width,
              };
              return usePortal ? (
                <PortalBaseTooltip {...tooltipProps}>
                  {renderTooltipContent({ isOpen: state.visible })}
                </PortalBaseTooltip>
              ) : (
                <BaseTooltip {...tooltipProps}>{renderTooltipContent({ isOpen: state.visible })}</BaseTooltip>
              );
            }}
          </Transition>
        </div>
      </div>
    );
  }
);

HidingTooltip.displayName = 'HidingTooltip';
